/* ==========================================================================
   Intake List Page
   Task F36: Internal page listing intake submissions with filters
   ========================================================================== */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Copy, Check, RefreshCw, Clock, AlertCircle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import {
  listIntakeSubmissions,
  listPendingSubscriptions,
  resendIntakeEmail,
  type IntakeListItem,
  type IntakeStatus,
  type PendingSubscriptionItem,
} from '@/api/internal'
import { ApiClientError } from '@/api/client'

/* --------------------------------------------------------------------------
   Status Badge
   -------------------------------------------------------------------------- */

const STATUS_STYLES: Record<IntakeStatus, string> = {
  submitted: 'bg-amber-100 text-amber-800',
  in_review: 'bg-blue-100 text-blue-800',
  provisioned: 'bg-purple-100 text-purple-800',
  live: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
}

const STATUS_LABELS: Record<IntakeStatus, string> = {
  submitted: 'Submitted',
  in_review: 'In Review',
  provisioned: 'Provisioned',
  live: 'Live',
  completed: 'Completed',
}

function StatusBadge({ status }: { status: IntakeStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-800'}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  )
}

/* --------------------------------------------------------------------------
   Intake Link Badge
   -------------------------------------------------------------------------- */

function IntakeLinkBadge({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Clock className="h-3 w-3" />
        Active
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      <AlertCircle className="h-3 w-3" />
      Expired
    </span>
  )
}

/* --------------------------------------------------------------------------
   Status Filter Tabs
   -------------------------------------------------------------------------- */

type TabValue = 'awaiting_intake' | IntakeStatus | 'all'

const STATUS_TABS: { value: TabValue; label: string }[] = [
  { value: 'awaiting_intake', label: 'Awaiting Intake' },
  { value: 'all', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'provisioned', label: 'Provisioned' },
  { value: 'live', label: 'Live' },
  { value: 'completed', label: 'Completed' },
]

/* --------------------------------------------------------------------------
   Pending Subscriptions Table
   -------------------------------------------------------------------------- */

function PendingSubscriptionsTable({
  items,
  onResend,
}: {
  items: PendingSubscriptionItem[]
  onResend: (id: string) => void
}) {
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)

  const handleCopyLink = async (item: PendingSubscriptionItem) => {
    if (!item.intake_token) return
    const intakeUrl = `${window.location.origin}/intake/${item.intake_token}`
    try {
      await navigator.clipboard.writeText(intakeUrl)
      setCopiedId(item.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      // Clipboard API not available
    }
  }

  const handleResend = async (item: PendingSubscriptionItem) => {
    try {
      setResendingId(item.id)
      await onResend(item.id)
    } finally {
      setResendingId(null)
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left px-4 py-3 font-medium text-charcoal/70">
              Email
            </th>
            <th className="text-left px-4 py-3 font-medium text-charcoal/70 hidden sm:table-cell">
              Tier
            </th>
            <th className="text-left px-4 py-3 font-medium text-charcoal/70 hidden md:table-cell">
              Billing
            </th>
            <th className="text-left px-4 py-3 font-medium text-charcoal/70 hidden md:table-cell">
              Paid
            </th>
            <th className="text-left px-4 py-3 font-medium text-charcoal/70">
              Intake Link
            </th>
            <th className="text-left px-4 py-3 font-medium text-charcoal/70">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <td className="px-4 py-3 font-medium text-charcoal">
                {item.contact_email}
              </td>
              <td className="px-4 py-3 text-charcoal/70 hidden sm:table-cell capitalize">
                {item.tier}
              </td>
              <td className="px-4 py-3 text-charcoal/70 hidden md:table-cell capitalize">
                {item.billing_cycle}
              </td>
              <td className="px-4 py-3 text-charcoal/70 hidden md:table-cell">
                {new Date(item.created_at).toLocaleDateString()}
              </td>
              <td className="px-4 py-3">
                <IntakeLinkBadge active={item.intake_token_active} />
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  {item.intake_token_active ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCopyLink(item)}
                    >
                      {copiedId === item.id ? (
                        <>
                          <Check className="h-3 w-3 mr-1" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3 mr-1" />
                          Copy Link
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={resendingId === item.id}
                      onClick={() => handleResend(item)}
                    >
                      <RefreshCw
                        className={`h-3 w-3 mr-1 ${resendingId === item.id ? 'animate-spin' : ''}`}
                      />
                      Resend
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Main Component
   -------------------------------------------------------------------------- */

export function IntakeList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [items, setItems] = useState<IntakeListItem[]>([])
  const [pendingItems, setPendingItems] = useState<PendingSubscriptionItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [resendSuccess, setResendSuccess] = useState<string>()

  const activeTab = (searchParams.get('tab') as TabValue | null) || 'awaiting_intake'
  const activeStatus =
    activeTab !== 'awaiting_intake' && activeTab !== 'all'
      ? (activeTab as IntakeStatus)
      : undefined
  const searchQuery = searchParams.get('search') || ''
  const PAGE_SIZE = 20

  const isAwaitingIntakeTab = activeTab === 'awaiting_intake'

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(undefined)
    setResendSuccess(undefined)

    try {
      if (isAwaitingIntakeTab) {
        const result = await listPendingSubscriptions({
          search: searchQuery || undefined,
          page,
          page_size: PAGE_SIZE,
        })
        setPendingItems(result.items)
        setItems([])
        setTotal(result.total)
      } else {
        const result = await listIntakeSubmissions({
          status: activeStatus,
          search: searchQuery || undefined,
          page,
          page_size: PAGE_SIZE,
        })
        setItems(result.items)
        setPendingItems([])
        setTotal(result.total)
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          navigate('/internal/login', { replace: true })
          return
        }
        setError(err.getUserMessage())
      } else {
        setError('Failed to load data')
      }
    } finally {
      setLoading(false)
    }
  }, [isAwaitingIntakeTab, activeStatus, searchQuery, page, navigate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleTabChange = (tab: TabValue) => {
    const params = new URLSearchParams()
    if (tab !== 'awaiting_intake') {
      params.set('tab', tab)
    }
    // Preserve search if present
    if (searchQuery) {
      params.set('search', searchQuery)
    }
    setSearchParams(params)
    setPage(1)
  }

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('search', value)
    } else {
      params.delete('search')
    }
    setSearchParams(params)
    setPage(1)
  }

  const handleResend = async (subscriptionId: string) => {
    setError(undefined)
    setResendSuccess(undefined)
    try {
      await resendIntakeEmail(subscriptionId)
      setResendSuccess('Intake email resent successfully')
      // Refresh the list to update token status
      await fetchData()
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.getUserMessage())
      } else {
        setError('Failed to resend intake email')
      }
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="font-serif text-2xl text-charcoal">Intake Submissions</h1>
        <p className="text-sm text-charcoal/60 mt-1">
          Review and manage pharmacy onboarding submissions
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Status tabs */}
        <div className="flex gap-1 overflow-x-auto">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors
                ${
                  tab.value === activeTab
                    ? 'bg-deep-purple text-white'
                    : 'text-charcoal/70 hover:bg-gray-100'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative sm:ml-auto sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={
              isAwaitingIntakeTab ? 'Search by email...' : 'Search name or email...'
            }
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Success message */}
      {resendSuccess && (
        <Card className="p-4 mb-6 border-green-200 bg-green-50">
          <p className="text-sm text-green-700">{resendSuccess}</p>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="p-4 mb-6 border-red-200 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-lavender border-t-deep-purple" />
            <p className="mt-2 text-sm text-charcoal/60">Loading...</p>
          </div>
        ) : isAwaitingIntakeTab ? (
          pendingItems.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-charcoal/60">No pending subscriptions awaiting intake</p>
            </div>
          ) : (
            <PendingSubscriptionsTable
              items={pendingItems}
              onResend={handleResend}
            />
          )
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-charcoal/60">No intake submissions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-charcoal/70">
                    Pharmacy Name
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-charcoal/70 hidden sm:table-cell">
                    Admin Email
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-charcoal/70 hidden md:table-cell">
                    Slug
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-charcoal/70">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-charcoal/70 hidden lg:table-cell">
                    Tier
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-charcoal/70 hidden md:table-cell">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/internal/intake/${item.id}`)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-charcoal">
                      {item.pharmacy_name}
                    </td>
                    <td className="px-4 py-3 text-charcoal/70 hidden sm:table-cell">
                      {item.admin_email}
                    </td>
                    <td className="px-4 py-3 text-charcoal/70 hidden md:table-cell font-mono text-xs">
                      {item.preferred_slug}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-3 text-charcoal/70 hidden lg:table-cell capitalize">
                      {item.tier || '—'}
                    </td>
                    <td className="px-4 py-3 text-charcoal/70 hidden md:table-cell">
                      {new Date(item.submitted_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-charcoal/60">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}

export default IntakeList
